import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

const guideLinks = [
  { label: "Shopping Guide", slug: "pandabuy-sheet-guide" },
  { label: "Shoes Comparison", slug: "pandabuy-sheet-shoes-comparison" },
  { label: "Hoodies & Sweaters", slug: "pandabuy-sheet-hoodies-sweaters" },
  { label: "T-Shirts Buying", slug: "pandabuy-sheet-tshirts-buying" },
  { label: "Jackets Guide", slug: "pandabuy-sheet-jackets-guide" },
  { label: "Pants & Shorts", slug: "pandabuy-sheet-pants-shorts" },
  { label: "Accessories Tips", slug: "pandabuy-sheet-accessories-tips" },
  { label: "Bags & Backpacks", slug: "pandabuy-sheet-bags-backpacks" },
  { label: "Perfumes Guide", slug: "pandabuy-sheet-perfumes-fragrance" },
  { label: "Shopping Hacks", slug: "pandabuy-sheet-shopping-hacks" },
];

export default function Navbar() {
  const [catOpen, setCatOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const textColor = scrolled ? "text-foreground-800" : "text-white";
  const textHover = scrolled ? "hover:text-foreground-950" : "hover:text-white/90";
  const logoColor = scrolled ? "text-foreground-950" : "text-white";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background-50/95 backdrop-blur-sm" : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-4 md:px-8 py-3.5 md:py-4">
        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          <span className={`font-heading text-lg md:text-xl font-semibold ${logoColor} tracking-tight transition-colors duration-300`}>
            PANDABUY<span className={scrolled ? "text-primary-500" : "text-white/90"}>.</span>TATTOO
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/"
            className={`text-sm font-semibold ${textColor} ${textHover} transition-colors whitespace-nowrap cursor-pointer`}
          >
            Home
          </Link>

          <div className="relative" ref={catRef}>
            <button
              onClick={() => { setCatOpen(!catOpen); setGuideOpen(false); }}
              className={`flex items-center gap-1 text-sm font-semibold ${textColor} ${textHover} transition-colors whitespace-nowrap cursor-pointer`}
            >
              Categories
              <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${catOpen ? "rotate-180" : ""}`}></i>
            </button>

            {catOpen && (
              <div className="absolute top-full mt-2 left-0 bg-background-50 rounded-lg py-2 w-64 max-h-80 overflow-y-auto border border-background-200/70 shadow-sm">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="relative" ref={guideRef}>
            <button
              onClick={() => { setGuideOpen(!guideOpen); setCatOpen(false); }}
              className={`flex items-center gap-1 text-sm font-semibold ${textColor} ${textHover} transition-colors whitespace-nowrap cursor-pointer`}
            >
              Shopping Guide
              <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${guideOpen ? "rotate-180" : ""}`}></i>
            </button>

            {guideOpen && (
              <div className="absolute top-full mt-2 left-0 bg-background-50 rounded-lg py-2 w-56 max-h-80 overflow-y-auto border border-background-200/70 shadow-sm">
                {guideLinks.map((g) => (
                  <Link
                    key={g.slug}
                    to={`/guide/${g.slug}`}
                    onClick={() => setGuideOpen(false)}
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {g.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`text-sm font-semibold ${textColor} ${textHover} transition-colors whitespace-nowrap cursor-pointer`}
          >
            Spreadsheets
          </a>
        </div>

        <div className="flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center px-4 py-2 text-sm font-semibold rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Pandabuy Sheet
          </a>

          <button
            className={`md:hidden flex items-center justify-center w-9 h-9 cursor-pointer ${scrolled ? "text-foreground-950" : "text-white"}`}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-accent-100 rounded-md transition-colors cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-home-line text-base w-5 h-5 flex items-center justify-center"></i>
              Home
            </Link>

            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-3 pt-3 pb-1">
              Categories
            </p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-700 hover:bg-accent-100 rounded-md transition-colors cursor-pointer"
                  onClick={() => setMobileOpen(false)}
                >
                  <i className={`${cat.icon} text-sm w-5 h-5 flex items-center justify-center`}></i>
                  <span className="truncate">{cat.name}</span>
                </a>
              ))}
            </div>

            <hr className="border-background-200/70 my-2" />

            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-3 pt-1 pb-1">
              Shopping Guides
            </p>
            {guideLinks.map((g) => (
              <Link
                key={g.slug}
                to={`/guide/${g.slug}`}
                className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-700 hover:bg-accent-100 rounded-md transition-colors cursor-pointer"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-article-line text-sm w-5 h-5 flex items-center justify-center"></i>
                {g.label}
              </Link>
            ))}

            <hr className="border-background-200/70 my-2" />

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-primary-600 hover:bg-accent-100 rounded-md transition-colors cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-external-link-line text-sm w-5 h-5 flex items-center justify-center"></i>
              Pandabuy Sheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}